import { z } from "zod";
import { Timestamp } from "./common.ts";
import { gridBilinear } from "./weather-grid.ts";
import { WIND_ATTRIBUTION, WindGrid } from "./wind.ts";

/**
 * Precipitation overlay for the overview map ("Niederschlag"; legend "Regen und Schnee"): a MODEL
 * FORECAST, never radar. Owner (2026-10-29): free, no extra upstream calls.
 *
 * Source: the same MeteoSwiss ICON-CH refresh as the wind overlay (wind.ts). The API adds
 * Open-Meteo's hourly `precipitation` to the SAME request (3 variables; Open-Meteo counts a
 * request as more than one call only above 10 variables), so the budget, the refresh cadence and
 * the call counter in wind.ts apply unchanged. `fetchedAt`, `runAt` and `grid` are those of the
 * wind field of the same refresh. Same lake-level `elevation` per point. Same credit.
 * `times` are the ENDS of the hourly sums: the API trims them so `times[0]` is the end of the
 * current hour (H+1 for a request during [H, H+1)), and keeps every later fetched hour. The fetch
 * ends at the wind field's last hour, so the wind slider's last step has no sum (see "Slider").
 *
 * Values: `p[k][index]` (index = j·nx + i, row-major from the south-west, like `windGridPoints`)
 * is rain plus snow (as water) in PRECIP_UNIT_MM = 0.1 mm integers, summed over the HOUR BEFORE
 * `times[k]`: the value at 14:00 is what falls from 13:00 to 14:00. A missing value is `null`,
 * never 0 (0 means dry); upstream values above PRECIP_MAX_UNITS are junk and become null.
 *
 * API rules (binding):
 * - Precipitation never fails, delays or blocks the wind refresh. Nulls are per point and hour:
 *   a missing, negative, non-finite or junk value is null at that point and hour only. `p` is
 *   all-null for a refresh only when no location carries the `precipitation` array at all; the
 *   route then still answers 200 (the client says "no data", never dry).
 * - The precipitation field is cached, persisted and replaced ONLY together with the wind field
 *   of the same refresh (both validate before either is assigned).
 * - A persisted cache file from before this overlay (no precipitation) restores the wind as usual
 *   and leaves precipitation empty: the route answers 503 until the next REGULAR refresh. It never
 *   triggers an early refetch.
 * - Rate limit like wind (`rateLimit` 60/min per IP), with its own key `precip`. The route is
 *   served from memory with an ETag and never triggers an upstream request by itself (only the
 *   shared lazy wind refresh does).
 *
 * Time: precipitation is an hourly sum, so it is NOT interpolated in time. At a time t the overlay
 * shows the hour [H, H+1) that contains t (H = t rounded down to the full UTC hour), i.e. the
 * value at `times[k] = H+1`, labelled "H–H+1". `precipHourAt` picks it. Space is bilinear like
 * wind (drawn smooth; the credit says "interpoliert"). `times` are on whole UTC hours.
 *
 * Slider (client): the time bar shows while wind OR precipitation is on, with one shared time.
 * - Precipitation only: the range is `precipTimeRange(times)` = [times[0] − 1 h, last − 1 h], so
 *   the default "now" lands on the current hour's sum and every step has one. The client does NOT
 *   fetch the wind field for this.
 * - Wind on (with or without precipitation): the wind field's range from the current hour on
 *   (`max(times[0], current hour)`; its past hours serve only measured radar steps, radar.ts);
 *   precipitation shows "no data" at any step without a sum (typically the last), never dry.
 */
/** One integer unit of `p` in mm (per hour). */
export const PRECIP_UNIT_MM = 0.1;
/** Sanity bound in PRECIP_UNIT_MM units (150 mm in one hour). */
export const PRECIP_MAX_UNITS = 1500;
/**
 * Class edges in mm/h for the `precip-1..4` tokens: < 0.1 nothing is drawn (dry); 0.1–1 light,
 * 1–4 moderate, 4–10 heavy, ≥ 10 very heavy. Intensity is never colour-only: the legend names
 * the classes and the readout gives mm/h.
 */
export const PRECIP_CLASSES_MM_H = [0.1, 1, 4, 10] as const;
export const PRECIP_ATTRIBUTION = WIND_ATTRIBUTION;

const Hour = z.array(z.number().int().min(0).max(PRECIP_MAX_UNITS).nullable());

/** GET /api/weather/precip (public). */
export const PrecipField = z
  .object({
    model: z.literal("meteoswiss-icon-ch"),
    /** When the API fetched this field from upstream (same refresh as wind). */
    fetchedAt: Timestamp,
    /** Model run (initialisation time), null when upstream did not say. */
    runAt: Timestamp.nullable(),
    /** End of each hourly sum (UTC, ascending). */
    times: z.array(Timestamp).min(1),
    grid: WindGrid,
    p: z.array(Hour),
    attribution: z.string(),
  })
  .strict()
  .superRefine((f, ctx) => {
    const n = f.grid.nx * f.grid.ny;
    if (f.p.length !== f.times.length)
      ctx.addIssue({ code: "custom", path: ["p"], message: "one array per time" });
    f.p.forEach((a, k) => {
      if (a.length !== n)
        ctx.addIssue({ code: "custom", path: ["p", k], message: `expected ${n} values` });
    });
    f.times.forEach((t, k) => {
      if (Date.parse(t) % 3_600_000 !== 0)
        ctx.addIssue({ code: "custom", path: ["times", k], message: "whole UTC hours only" });
      if (k > 0 && Date.parse(t) <= Date.parse(f.times[k - 1]!))
        ctx.addIssue({ code: "custom", path: ["times", k], message: "times must ascend" });
    });
  });
export type PrecipField = z.infer<typeof PrecipField>;

/** Upstream mm (one hour) → PRECIP_UNIT_MM integer; null for missing, negative or junk values. */
export function precipUnits(mm: number | null | undefined): number | null {
  if (mm === null || mm === undefined || !Number.isFinite(mm) || mm < 0) return null;
  const n = Math.round(mm / PRECIP_UNIT_MM);
  return n > PRECIP_MAX_UNITS ? null : n;
}

/** Class index for mm/h: -1 dry (draw nothing), 0 light, 1 moderate, 2 heavy, 3 very heavy. */
export function precipClass(mmPerHour: number): -1 | 0 | 1 | 2 | 3 {
  const [a, b, c, d] = PRECIP_CLASSES_MM_H;
  if (!(mmPerHour >= a)) return -1;
  if (mmPerHour < b) return 0;
  if (mmPerHour < c) return 1;
  if (mmPerHour < d) return 2;
  return 3;
}

/**
 * The hour shown at time `tMs`: the sum [H, H+1) containing it, stored at `times[k] = H+1`.
 * Returns k and the hour's bounds, or null when the field does not cover it (NaN, before the
 * first sum or after the last).
 */
export function precipHourAt(
  times: readonly string[],
  tMs: number,
): { k: number; fromMs: number; toMs: number } | null {
  if (!Number.isFinite(tMs)) return null;
  const fromMs = Math.floor(tMs / 3_600_000) * 3_600_000;
  const toMs = fromMs + 3_600_000;
  const k = times.findIndex((t) => Date.parse(t) === toMs);
  return k < 0 ? null : { k, fromMs, toMs };
}

/**
 * Slider range when only precipitation is on: from the start of the first sum's hour to the start
 * of the last sum's hour (epoch ms), so every step has a sum. Null for an empty list.
 */
export function precipTimeRange(
  times: readonly string[],
): { startMs: number; endMs: number } | null {
  if (times.length === 0) return null;
  return {
    startMs: Date.parse(times[0]!) - 3_600_000,
    endMs: Date.parse(times[times.length - 1]!) - 3_600_000,
  };
}

/**
 * Precipitation at (lon, lat) for the hour containing `tMs`, in mm/h (bilinear in space, no
 * time interpolation). Null outside the grid, outside the covered hours, or wherever a needed
 * corner is missing (draw nothing; never treat as dry).
 *
 * `createPrecipSampler` parses `times` once; use it when drawing.
 */
export function createPrecipSampler(
  field: Pick<PrecipField, "grid" | "times" | "p">,
): (lon: number, lat: number, tMs: number) => number | null {
  const ends = field.times.map((t) => Date.parse(t));
  return (lon, lat, tMs) => {
    if (!Number.isFinite(tMs)) return null;
    const k = ends.indexOf(Math.floor(tMs / 3_600_000) * 3_600_000 + 3_600_000);
    if (k < 0) return null;
    const s = gridBilinear(field.grid, field.p[k]!, lon, lat);
    return s === null ? null : s * PRECIP_UNIT_MM;
  };
}

export function samplePrecip(
  field: Pick<PrecipField, "grid" | "times" | "p">,
  lon: number,
  lat: number,
  tMs: number,
): number | null {
  return createPrecipSampler(field)(lon, lat, tMs);
}
